import { Alert, Box, Checkbox, FormControlLabel, Popover } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Holiday, HolidayClinic } from "../../models/Holiday";
import { useCallback, useMemo, useState } from "react";
import { isBefore } from "date-fns/isBefore";
import { isValid } from "date-fns/isValid";
import { parse } from "date-fns/parse";
import { startOfToday } from "date-fns/startOfToday";
import { DeleteHolidayModal } from "./DeleteHolidayModal";
import { UpdateHolidayModal } from "./UpdateHolidayModal";
import { useClinicContext } from "../../contexts";

interface HolidaysTableProps {
	holidays: Holiday[];
	onModalClose: (isUpdated: boolean) => void;
}

export const HolidaysTable = ({
	holidays,
	onModalClose
}: HolidaysTableProps) => {
	const { clinics } = useClinicContext();
	const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
	const [displayEditModal, setDisplayEditModal] = useState(false);
	const [selectedHoliday, setSelectedHoliday] = useState<Holiday | undefined>(
		undefined
	);
	const [stateFilterAnchorEl, setStateFilterAnchorEl] =
		useState<HTMLElement | null>(null);
	const isStateFilterOpen = Boolean(stateFilterAnchorEl);
	const [filterStates, setFilterStates] = useState<Set<string>>(new Set());
	const isStateFilterActive = filterStates.size > 0;
	const [clinicFilterAnchorEl, setClinicFilterAnchorEl] =
		useState<HTMLElement | null>(null);
	const isClinicFilterOpen = Boolean(clinicFilterAnchorEl);

	const [filterClinicIds, setFilterClinicIds] = useState<Set<string>>(
		new Set()
	);
	const isClinicFilterActive = filterClinicIds.size > 0;
	const [excludePastHolidays, setExcludePastHolidays] = useState(true);
	const stateFilterIconClassName = `icon ch-filterIcon ${
		isStateFilterActive ? "ch-filterIconActive" : ""
	}`;
	const clinicFilterIconClassName = `icon ch-filterIcon ${
		isClinicFilterActive ? "ch-filterIconActive" : ""
	}`;

	const handleStateFilterClick = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			setStateFilterAnchorEl(event.currentTarget);
		},
		[]
	);

	const handleStateFilterClose = useCallback(
		() => setStateFilterAnchorEl(null),
		[]
	);

	const handleClinicFilterClick = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			setClinicFilterAnchorEl(event.currentTarget);
		},
		[]
	);

	const handleClinicFilterClose = useCallback(
		() => setClinicFilterAnchorEl(null),
		[]
	);

	const clinicStates = useMemo(() => {
		if (!clinics) {
			return [];
		}
		const states = new Set(clinics.flatMap((clinic) => clinic.state));
		return Array.from(states).sort() as string[];
	}, [clinics]);

	const handleStateFilterToggle = useCallback((state: string) => {
		setFilterStates((prev) => {
			const next = new Set(prev);
			next.has(state) ? next.delete(state) : next.add(state);
			return next;
		});
	}, []);

	const handleClearStateFilters = useCallback(() => {
		setFilterStates(new Set());
	}, []);

	const handleClinicFilterToggle = useCallback((clinicKey: string) => {
		setFilterClinicIds((prev) => {
			const next = new Set(prev);
			next.has(clinicKey) ? next.delete(clinicKey) : next.add(clinicKey);
			return next;
		});
	}, []);

	const handleClearClinicFilters = useCallback(() => {
		setFilterClinicIds(new Set());
	}, []);

	const displayedHolidays = useMemo(() => {
		const today = startOfToday();

		return holidays.filter((holiday) => {
			const holidayDate = parse(holiday.date, "dd/MM/yyyy", new Date());
			const isPastHoliday =
				isValid(holidayDate) && isBefore(holidayDate, today);

			return (
				(!excludePastHolidays || holiday.recurring || !isPastHoliday) &&
				(filterStates.size === 0 ||
					holiday.clinics.some((clinic: HolidayClinic) =>
						filterStates.has(clinic.state)
					)) &&
				(filterClinicIds.size === 0 ||
					holiday.clinics.some((clinic: HolidayClinic) =>
						filterClinicIds.has(clinic.clinicId.toString())
					))
			);
		});
	}, [excludePastHolidays, holidays, filterClinicIds, filterStates]);

	const handleDeleteClick = useCallback((holiday: Holiday) => {
		setSelectedHoliday(holiday);
		setDisplayDeleteModal(true);
	}, []);

	const closeDeleteConfirmModal = useCallback(
		(isUpdated: boolean) => {
			setDisplayDeleteModal(false);
			setSelectedHoliday(undefined);
			onModalClose(isUpdated);
		},
		[onModalClose]
	);

	const handleEditClick = useCallback((holiday: Holiday) => {
		setSelectedHoliday(holiday);
		setDisplayEditModal(true);
	}, []);

	const closeEditModal = useCallback(
		(isUpdated: boolean) => {
			setDisplayEditModal(false);
			setSelectedHoliday(undefined);
			onModalClose(isUpdated);
		},
		[onModalClose]
	);

	const holidayStates = useCallback((holiday: Holiday) => {
		if (!holiday) {
			return [];
		}
		const states = new Set(holiday.clinics.flatMap((clinic) => clinic.state));
		return Array.from(states).sort() as string[];
	}, []);

	const clinicNames = useCallback((holiday: Holiday) => {
		if (!holiday) {
			return [];
		}
		const names = new Set(
			holiday.clinics.flatMap((clinic) => clinic.clinicName)
		);
		return Array.from(names).sort() as string[];
	}, []);

	return (
		<>
			<FormControlLabel
				control={
					<Checkbox
						checked={excludePastHolidays}
						onChange={(event) => setExcludePastHolidays(event.target.checked)}
					/>
				}
				label="Exclude past holidays"
			/>
			<table
				aria-label="holidays"
				className="ch-table"
				style={{ minWidth: 650 }}
			>
				<thead>
					<tr>
						<th>Description</th>
						<th>Date</th>
						<th>Recurring</th>
						<th>
							<div className="flex items-center gap-2">
								State
								<span
									onClick={handleStateFilterClick}
									style={{ display: "flex" }}
								>
									<FilterListIcon className={stateFilterIconClassName} />
								</span>
								{isStateFilterActive && (
									<span
										className="ch-clearFilter"
										onClick={handleClearStateFilters}
									>
										(clear)
									</span>
								)}
							</div>
						</th>
						<th>
							<div className="flex items-center gap-2">
								Centre
								<span
									onClick={handleClinicFilterClick}
									style={{ display: "flex" }}
								>
									<FilterListIcon className={clinicFilterIconClassName} />
								</span>
								{isClinicFilterActive && (
									<span
										className="ch-clearFilter"
										onClick={handleClearClinicFilters}
									>
										(clear)
									</span>
								)}
							</div>
						</th>
						<th style={{ width: 100 }}>Actions</th>
					</tr>
				</thead>
				<tbody>
					{displayedHolidays.length > 0 ? (
						displayedHolidays.map((holiday) => (
							<tr key={holiday.id}>
								<td>{holiday.description}</td>
								<td>{holiday.date}</td>
								<td>{holiday.recurring ? "Recurring" : ""}</td>
								<td>{holidayStates(holiday).join(", ")}</td>
								<td>{clinicNames(holiday).join(", ")}</td>
								<td>
									<div className="flex">
										<EditOutlinedIcon
											className="icon"
											style={{ marginRight: 10 }}
											onClick={() => handleEditClick(holiday)}
										/>
										<DeleteOutlineIcon
											className="icon"
											onClick={() => handleDeleteClick(holiday)}
										/>
									</div>
								</td>
							</tr>
						))
					) : (
						<tr>
							<td colSpan={7} style={{ padding: 0 }}>
								<Alert severity="info" style={{ borderRadius: 0 }}>
									No holidays found for the selected filters
								</Alert>
							</td>
						</tr>
					)}
				</tbody>
			</table>

			<DeleteHolidayModal
				open={displayDeleteModal}
				holiday={selectedHoliday}
				onClose={closeDeleteConfirmModal}
			/>
			<UpdateHolidayModal
				open={displayEditModal}
				mode="edit"
				holiday={selectedHoliday}
				onClose={closeEditModal}
			/>

			<Popover
				open={isStateFilterOpen}
				anchorEl={stateFilterAnchorEl}
				onClose={handleStateFilterClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				<Box sx={{ p: 2, minWidth: 150 }}>
					<div className="flex flex-col gap-1">
						{clinicStates.map((state) => (
							<div
								key={state}
								className="flex items-center cursor-pointer"
								onClick={() => handleStateFilterToggle(state)}
							>
								<Checkbox checked={filterStates.has(state)} size="small" />
								<span>{state}</span>
							</div>
						))}
					</div>
				</Box>
			</Popover>

			<Popover
				open={isClinicFilterOpen}
				anchorEl={clinicFilterAnchorEl}
				onClose={handleClinicFilterClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				<Box sx={{ p: 2, minWidth: 200 }}>
					<div className="flex flex-col gap-1">
						{clinics?.map((clinic) => (
							<div
								key={clinic.id}
								className="flex items-center cursor-pointer"
								onClick={() => handleClinicFilterToggle(clinic.id.toString())}
							>
								<Checkbox
									checked={filterClinicIds.has(clinic.id.toString())}
									size="small"
								/>
								<span>{clinic.name}</span>
							</div>
						))}
					</div>
				</Box>
			</Popover>
		</>
	);
};
